import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";

export default function AdminDashboard() {
  const [tab, setTab] = useState("customers");
  const [stats, setStats] = useState({});
  const [customers, setCustomers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/customers"),
      api.get("/admin/partners"),
      api.get("/admin/leads"),
    ]).then(([s, c, p, l]) => {
      setStats(s.data);
      setCustomers(c.data);
      setPartners(p.data);
      setLeads(l.data);
    });
  }, []);

  const tabs = [
    { id: "customers", label: `Customers (${stats.customers ?? 0})` },
    { id: "partners", label: `Partners (${stats.partners ?? 0})` },
    { id: "leads", label: `Leads (${stats.leads ?? 0})` },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar variant="dashboard" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="text-xs uppercase tracking-widest text-[#1E3F20] font-semibold">
          Admin Console
        </div>
        <h1 className="font-display text-4xl mt-2">OCTA Services Operations</h1>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-8">
          {[
            { label: "Customers", value: stats.customers || 0 },
            { label: "Partners", value: stats.partners || 0 },
            { label: "Total Leads", value: stats.leads || 0 },
            { label: "Open Leads", value: stats.open_leads || 0 },
            { label: "Assigned", value: stats.assigned_leads || 0 },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-[#E5E3DB] rounded-xl p-4" data-testid={`admin-stat-${s.label.toLowerCase()}`}>
              <div className="text-xs text-[#737373] uppercase tracking-widest font-semibold">{s.label}</div>
              <div className="font-display text-3xl mt-2">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="mt-10 flex gap-2 border-b border-[#E5E3DB]">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                tab === t.id
                  ? "border-[#1E3F20] text-[#1A1A1A]"
                  : "border-transparent text-[#737373] hover:text-[#1A1A1A]"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tables */}
        <div className="mt-6 bg-white border border-[#E5E3DB] rounded-2xl overflow-hidden">
          {tab === "customers" && (
            <table className="w-full text-sm" data-testid="admin-customers-table">
              <thead className="bg-[#F3F1ED] text-left text-xs uppercase tracking-widest text-[#737373]">
                <tr>
                  <th className="p-4">Name</th>
                  <th className="p-4">Mobile</th>
                  <th className="p-4">Location</th>
                  <th className="p-4">Address</th>
                  <th className="p-4">Joined</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c, i) => (
                  <tr key={c.id} className={i % 2 ? "bg-[#F9F8F6]" : "bg-white"}>
                    <td className="p-4 font-medium">{c.name}</td>
                    <td className="p-4">{c.mobile}</td>
                    <td className="p-4">{c.location}</td>
                    <td className="p-4 text-[#737373]">{c.address}</td>
                    <td className="p-4 text-[#737373]">{new Date(c.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {customers.length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center text-[#737373]">No customers yet</td></tr>
                )}
              </tbody>
            </table>
          )}

          {tab === "partners" && (
            <table className="w-full text-sm" data-testid="admin-partners-table">
              <thead className="bg-[#F3F1ED] text-left text-xs uppercase tracking-widest text-[#737373]">
                <tr>
                  <th className="p-4">Name</th>
                  <th className="p-4">Mobile</th>
                  <th className="p-4">Pincode</th>
                  <th className="p-4">Radius</th>
                  <th className="p-4">Services</th>
                  <th className="p-4">Aadhar</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((p, i) => (
                  <tr key={p.id} className={i % 2 ? "bg-[#F9F8F6]" : "bg-white"}>
                    <td className="p-4 font-medium">{p.name}</td>
                    <td className="p-4">{p.mobile}</td>
                    <td className="p-4">{p.service_pincode}</td>
                    <td className="p-4">{p.service_radius_km} km</td>
                    <td className="p-4 text-[#737373]">{(p.services || []).length}</td>
                    <td className="p-4 text-[#737373]">{p.aadhar_number}</td>
                  </tr>
                ))}
                {partners.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-[#737373]">No partners yet</td></tr>
                )}
              </tbody>
            </table>
          )}

          {tab === "leads" && (
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead className="bg-[#F3F1ED] text-left text-xs uppercase tracking-widest text-[#737373]">
                <tr>
                  <th className="p-4">Service</th>
                  <th className="p-4">Customer</th>
                  <th className="p-4">Pincode</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Responses</th>
                  <th className="p-4">Created</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l, i) => (
                  <tr key={l.id} className={i % 2 ? "bg-[#F9F8F6]" : "bg-white"}>
                    <td className="p-4 font-medium">{l.service_name}</td>
                    <td className="p-4">{l.customer_name} ({l.customer_mobile})</td>
                    <td className="p-4">{l.pincode}</td>
                    <td className="p-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${l.status === "open" ? "bg-[#F3F1ED]" : "bg-[#1E3F20] text-white"}`}>
                        {l.status}
                      </span>
                    </td>
                    <td className="p-4">{(l.responded_partners || []).length}</td>
                    <td className="p-4 text-[#737373]">{new Date(l.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {leads.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-[#737373]">No leads yet</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
